import { Typography } from '@mui/material';
import { clsx } from 'clsx';
import React, { memo, useCallback, useMemo, useState } from 'react';

import chart from '~/assets/icons/chart.svg';
import chartMarker from '~/assets/icons/chart-marker.svg';
import { AdaPrediction } from '~/pages/Company/components/Inference/components/AdaPrediction';
import { ChartPopover } from '~/pages/Company/components/Inference/components/ChartPopover';
import { GetCompanyInferenceResponse } from '~/services/rtkQuery/company/types';

import styles from './styles.module.scss';

interface Props {
  data: GetCompanyInferenceResponse['result']['data']['investment_score'];
  irrTh: number;
  score: number;
  scatterChartData: GetCompanyInferenceResponse['result']['data']['supporting_graphs']['investment_score'];
}

const Component: React.FC<Props> = ({ data, irrTh, score, scatterChartData }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleChartButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(event => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleChartPopoverClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const labels = useMemo(() => {
    return data.quartiles.reduce<number[]>((acc, current, index, array) => {
      const newAcc = [...acc, current.bottom_th];

      if (index === array.length - 1) {
        newAcc.push(current.top_th);
      }

      return newAcc;
    }, []);
  }, [data.quartiles]);

  const renderBar = useCallback(
    (
      quartile: GetCompanyInferenceResponse['result']['data']['investment_score']['quartiles'][number],
      index: number,
    ) => {
      const names: Record<
        GetCompanyInferenceResponse['result']['data']['investment_score']['quartiles'][number]['name'],
        string
      > = {
        bottom: 'Bottom Quartile',
        third: 'Third Quartile',
        second: 'Second Quartile',
        top: 'Top Quartile',
      };

      return (
        <div
          className={clsx(styles['chart__bar'], {
            [styles['chart__bar--last']]: index === data.quartiles.length - 1,
            [styles['chart__bar--success']]: quartile.active && data.score >= data.rule,
            [styles['chart__bar--error']]: quartile.active && data.score < data.rule,
          })}
          key={quartile.name}
        >
          <Typography align="center" color="var(--text-3-color)" variant="subtitle2">
            Probability to outperform the market: {quartile.delta_to_market * 100}%
          </Typography>
          <div>
            {quartile.active && (
              <Typography color="var(--text-3-color)" fontWeight="600" variant="h3">
                {data.score.toFixed(1)}
              </Typography>
            )}
            <Typography color="var(--gray-2-color)" variant="subtitle2">
              {names[quartile.name]}
            </Typography>
          </div>
        </div>
      );
    },
    [data.quartiles.length, data.rule, data.score],
  );

  const renderLabel = useCallback((label: number, index: number) => {
    return (
      <Typography
        align="center"
        color="var(--gray-2-color)"
        fontWeight="300"
        key={label}
        left={`calc(${index * 25}% - 25px)`}
        position="absolute"
        variant="subtitle1"
        width="50px"
      >
        {label}
      </Typography>
    );
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.container__heading}>
        <Typography color="var(--text-3-color)" fontWeight="500" variant="subtitle1">
          Overall Investment Score
        </Typography>
        <button type="button" onClick={handleChartButtonClick}>
          <img alt="chart" src={chart} />
        </button>
        <ChartPopover anchorEl={anchorEl} onClose={handleChartPopoverClose}>
          <AdaPrediction
            className={styles['container__scatter-chart']}
            data={scatterChartData}
            irrTh={irrTh}
            score={score}
          />
        </ChartPopover>
      </div>
      <div className={styles.chart}>
        <div className={styles['chart__bars']}>{data.quartiles.map(renderBar)}</div>
        <div className={styles['chart__x-axis-line']} />
        <div className={styles['chart__x-axis-labels']}>{labels.map(renderLabel)}</div>
        <div
          className={styles['chart__investment-rule']}
          style={{
            left: `calc(${
              ((data.rule - labels[0]) / (labels[labels.length - 1] - labels[0])) * 100
            }% - 25px)`,
          }}
        >
          <Typography
            align="center"
            color="var(--text-3-color-60)"
            fontStyle="italic"
            mb="7px"
            variant="caption"
          >
            Investment Rule
          </Typography>
          <img alt="marker" src={chartMarker} />
          <Typography color="var(--text-3-color)" fontWeight="700" mt="11px" variant="subtitle1">
            {data.rule}
          </Typography>
        </div>
      </div>
    </div>
  );
};

export const OverallInvestmentScore = memo(Component);
